import { useEffect, useMemo, useState } from "react"

import { Divider, Flex, Space, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import Private from "../../Components/Private"
import Icon from "../../Icon"
//import EnrollmentPricingDetailsModal from './EnrollmentPricingDetailsModal'
import Images from "../../Images"
import {
  enrollmentsStatus,
  enrollmentStatusMap,
  formatToTwoDecimals,
  onboardingServiceImgGray,
  serviceImgGray
} from "../../Utils/utils"
import ButtonComponent from "../Button"
import EnrollmentArnCodesCard from "./EnrollmentArnCodesCard"
import EnrollmentBinCaidCodesCard from "./EnrollmentBinCaidCodesCard"
import EnrollmentDescriptorCard from "./EnrollmentDescriptorCard"

import "./EnrollmentCard.less"

const { Text } = Typography

const VerticalLine = () => {
  return (
    <div className="vertical-line-container">
      <Text className="optional-text" type="secondary">
        Optional
      </Text>
    </div>
  )
}

const EnrollmentCard = ({
  enrollment,
  status,
  style,
  isAdmin,
  onChangeStatus,
  onChangeStatusDescriptor,
  onChangeDescriptorType,
  theme,
  children,
  isOnboarding = false
}) => {
  const { t } = useTranslation()
  const name = enrollment.name.toLowerCase()
  const images = isOnboarding ? onboardingServiceImgGray : serviceImgGray
  const disconnectEnrollment = () => {
    onChangeStatus(enrollment?.id, "set_enrollment_as_unconnected")
  }

  const [currentView, setCurrentView] = useState("descriptors") // 'descriptors', 'arn_codes', 'bin_caid_codes'
  const [availableViews, setAvailableViews] = useState(["descriptors"])

  // Check available views based on enrollment data
  useEffect(() => {
    const views = ["descriptors"]

    if (enrollment?.visa_arn_codes?.length > 0) views.push("arn_codes")
    if (enrollment?.visa_bin_codes?.length > 0 || enrollment?.visa_caid_codes?.length > 0) {
      views.push("bin_caid_codes")
    }

    setAvailableViews(views)

    // if current view is no longer available, reset to 'descriptors'
    if (!views.includes(currentView)) {
      setCurrentView("descriptors")
    }
  }, [enrollment])

  const handleChangeView = (targetView) => {
    // if a specific view is provided, switch to it
    if (targetView && typeof targetView === "string") {
      setCurrentView(targetView)
      return
    }

    // if no specific view, cycle through available views
    const currentIndex = availableViews.indexOf(currentView)
    const nextIndex = (currentIndex + 1) % availableViews.length
    setCurrentView(availableViews[nextIndex])
  }

  const getButtonText = () => {
    const currentIndex = availableViews.indexOf(currentView)
    const nextView = availableViews[(currentIndex + 1) % availableViews.length]

    switch (nextView) {
      case "arn_codes":
        return "Show ARN codes"
      case "bin_caid_codes":
        return "Show VISA BIN/CAID codes"
      default:
        return "Show descriptors"
    }
  }

  const adminViewContent = useMemo(() => {
    if (currentView === "descriptors") {
      return (
        <EnrollmentDescriptorCard
          enrollment={enrollment}
          isAdmin={isAdmin}
          onChangeStatusDescriptor={onChangeStatusDescriptor}
          onChangeDescriptorType={onChangeDescriptorType}
        />
      )
    } else if (currentView === "arn_codes") {
      return <EnrollmentArnCodesCard enrollment={enrollment} theme={theme} />
    } else if (currentView === "bin_caid_codes") {
      return <EnrollmentBinCaidCodesCard enrollment={enrollment} theme={theme} />
    } else {
      return null
    }
  }, [currentView, enrollment, isAdmin, theme, onChangeStatusDescriptor, onChangeDescriptorType])

  return (
    <div className="card enrollment-card-container">
      <Flex
        vertical
        style={{ ...style, paddingTop: isOnboarding ? 16 : 46 }}
        className={cls("enrollment-card", {
          "enrollment-card-active": enrollment["is_recommended"] || status,
          [`status-${status}`]: status,
          "enrollment-card_admin": isAdmin
        })}
      >
        {(enrollment["is_recommended"] || status) && !isOnboarding && (
          <div
            className={cls("enrollment-card-header", {
              [`status-${status}`]: status
            })}
          >
            {status && typeof enrollmentStatusMap[status]?.icon === "string" ? (
              <Icon type={enrollmentStatusMap[status]?.icon} style={{ fontSize: 20 }} />
            ) : (
              enrollmentStatusMap[status]?.icon
            )}

            <Text strong>{status ? t(enrollmentStatusMap[status]?.text) : t("Recommended")}</Text>
          </div>
        )}
        <Flex vertical gap={16} style={{ flexGrow: 1 }}>
          <Flex justify="space-between">
            <Flex align="center" gap={8} justify="space-between" style={{ width: "100%" }}>
              <Flex align="center" gap={8}>
                <Text strong style={{ fontSize: 18, lineHeight: 1.4 }}>
                  {enrollment?.name}
                </Text>
                <img
                  src={Images[images[theme][enrollment?.name?.toLowerCase()]]}
                  alt={enrollment?.name}
                />
                {!isOnboarding ? <Text type="secondary">{enrollment?.brand}</Text> : null}
              </Flex>
              {isAdmin && status !== "enable" && enrollment?.billing_descriptors.length === 0 && (
                <ButtonComponent
                  type="link"
                  style={{ marginLeft: "auto", padding: 0, height: 22 }}
                  onClick={disconnectEnrollment}
                >
                  Turn off enrollment
                </ButtonComponent>
              )}
              {isOnboarding ? (
                <Text className={cls("enrollment-card-text", { [`${name}`]: name })}>
                  {enrollmentsStatus[name]}
                </Text>
              ) : null}
            </Flex>
          </Flex>

          <Flex gap={8} vertical>
            <Text strong style={{ fontSize: 20, lineHeight: 1.4 }}>
              <Private>
                $
                {enrollment.price?.min
                  ? enrollment.price?.min !== enrollment.price?.max
                    ? `${formatToTwoDecimals(+enrollment.price?.min)} - ${formatToTwoDecimals(+enrollment.price?.max)}`
                    : formatToTwoDecimals(+enrollment.price?.min)
                  : formatToTwoDecimals(+enrollment.price)}
              </Private>
            </Text>
            <Text type="secondary">{t("per alert")}</Text>

            {/*{(enrollment.name === 'RDR' && !isAdmin) && <EnrollmentPricingDetailsModal rdrPrices={enrollment?.['rdr_prices']}/>}*/}

            {isAdmin &&
              enrollment.name === "RDR" &&
              enrollment?.is_active &&
              availableViews.length > 1 && (
                <ButtonComponent
                  type="link"
                  style={{
                    marginLeft: "auto",
                    padding: 0,
                    height: 22,
                    position: "absolute",
                    top: 120,
                    right: 16
                  }}
                  onClick={() => handleChangeView()}
                >
                  {getButtonText()}
                </ButtonComponent>
              )}
          </Flex>

          {isAdmin ? (
            <>{adminViewContent}</>
          ) : (
            <>
              <Divider style={{ margin: 0 }} />
              {Object.entries(enrollment["list_of_characteristics"]).map(([key, value]) => (
                <Flex justify="space-between" key={key}>
                  <Space>
                    <Icon type={"check_simple"} className="green-icon" />
                    <Text>{key}</Text>
                  </Space>
                  {Array.isArray(value) ? (
                    <Flex gap={4}>
                      {value.map((v) => (
                        <Icon key={v} type={v} />
                      ))}
                    </Flex>
                  ) : (
                    <Text strong>{value}</Text>
                  )}
                </Flex>
              ))}
            </>
          )}

          <Flex align="flex-end" justify="center" style={{ flexGrow: "1" }}>
            {children}
          </Flex>
        </Flex>
      </Flex>
      {name === "rdr" && isOnboarding ? <VerticalLine /> : null}
    </div>
  )
}
export default EnrollmentCard
